// Squares grid rules (Squares Redesign v3.1.1 §5, §6, §10c
// utils/squaresGridEngine.js in v1 terms). The server owns every one of these
// decisions now: locking, the number draw, square ownership and limits. The
// phone only renders what contest-api answers.

import type { typeEpochMs } from "../domain/primitives";
import { ContestEngineInputError } from "../errors";
import type {
  typeSquareKey,
  typeSquaresCell,
  typeSquaresGridFacts,
  typeSquaresGridShape,
  typeSquaresGridSize,
  typeSquaresGridState,
  typeSquaresGridType,
  typeSquaresNumbers,
  typeSquaresOwner,
  typeSquaresPublishStatus,
} from "./types";

/** The board geometry for a grid size. 25 is 5x5, 50 is 5x10, 100 is 10x10. */
export function buildSquaresGridShape(
  size: typeSquaresGridSize,
): typeSquaresGridShape {
  const rowCount = size === 100 ? 10 : 5;
  const colCount = size === 25 ? 5 : 10;
  return {
    size,
    rowCount,
    colCount,
    digitsPerRow: 10 / rowCount,
    digitsPerCol: 10 / colCount,
  };
}

export function squareKeyFor(input: {
  row: number;
  col: number;
}): typeSquareKey {
  return `${String(input.row)}-${String(input.col)}`;
}

/** Null for anything that is not "row-col" inside the shape. */
export function parseSquareKey(input: {
  squareKey: string;
  shape: typeSquaresGridShape;
}): { row: number; col: number } | null {
  const match = /^(\d+)-(\d+)$/.exec(input.squareKey);
  if (match === null) {
    return null;
  }
  const row = Number(match[1]);
  const col = Number(match[2]);
  // "03-4" would name the same square as "3-4"; only the canonical key counts.
  if (
    row >= input.shape.rowCount ||
    col >= input.shape.colCount ||
    squareKeyFor({ row, col }) !== input.squareKey
  ) {
    return null;
  }
  return { row, col };
}

export type typeSquaresGridErrorCode =
  | "squaresBadRandoms"
  | "squaresBadNumbers"
  | "squaresBadDigit"
  | "squaresBadSquareKey";

function refuseInput(code: typeSquaresGridErrorCode, message: string): never {
  throw new ContestEngineInputError<typeSquaresGridErrorCode>({
    code,
    message,
  });
}

/** A digit permutation is 0..9, each exactly once. */
function assertNumbers(numbers: typeSquaresNumbers): void {
  const isPermutation = (digits: readonly number[]): boolean =>
    digits.length === 10 &&
    [...digits].sort((left, right) => left - right).every((d, i) => d === i);
  if (
    !isPermutation(numbers.rowNumbers) ||
    !isPermutation(numbers.colNumbers)
  ) {
    refuseInput(
      "squaresBadNumbers",
      "Squares numbers must each be 0 to 9 once.",
    );
  }
}

function shuffleDigits(randoms: readonly number[]): number[] {
  const isValid =
    randoms.length === 9 &&
    randoms.every(
      (value, index) =>
        Number.isInteger(value) && value >= 0 && value < 10 - index,
    );
  if (!isValid) {
    refuseInput(
      "squaresBadRandoms",
      "Squares randoms must be nine integers, entry i in [0, 10 - i).",
    );
  }
  const digits = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
  randoms.forEach((offset, index) => {
    const swapIndex = index + offset;
    [digits[index], digits[swapIndex]] = [
      digits[swapIndex] ?? swapIndex,
      digits[index] ?? index,
    ];
  });
  return digits;
}

/**
 * Draws the numbers. Pure: the caller passes the randomness in. Each of
 * `rowRandoms` and `colRandoms` holds nine integers, where entry i is in
 * [0, 10 - i), drawn by contest-api with crypto.randomInt. A Fisher-Yates
 * shuffle of 0..9 turns each into a permutation: step i swaps position i with
 * position i + randoms[i].
 */
export function drawSquaresNumbers(input: {
  rowRandoms: readonly number[];
  colRandoms: readonly number[];
}): typeSquaresNumbers {
  return {
    rowNumbers: shuffleDigits(input.rowRandoms),
    colNumbers: shuffleDigits(input.colRandoms),
  };
}

function assertDigit(digit: number): void {
  if (!(Number.isInteger(digit) && digit >= 0 && digit <= 9)) {
    refuseInput("squaresBadDigit", "A Squares digit is a whole number 0 to 9.");
  }
}

/**
 * The square holding a top digit and a side digit once numbers are drawn.
 * Columns run across the top and read `colNumbers`; rows run down the side
 * and read `rowNumbers`. On a 5-column board column c covers colNumbers[2c]
 * and colNumbers[2c + 1].
 */
export function findSquareForDigits(input: {
  shape: typeSquaresGridShape;
  numbers: typeSquaresNumbers;
  topDigit: number;
  sideDigit: number;
}): typeSquareKey {
  assertNumbers(input.numbers);
  assertDigit(input.topDigit);
  assertDigit(input.sideDigit);
  const { shape, numbers } = input;
  return squareKeyFor({
    row: Math.floor(
      numbers.rowNumbers.indexOf(input.sideDigit) / shape.digitsPerRow,
    ),
    col: Math.floor(
      numbers.colNumbers.indexOf(input.topDigit) / shape.digitsPerCol,
    ),
  });
}

/** The digits a square covers. A 25 square covers 2 top and 2 side digits. */
export function squareDigits(input: {
  shape: typeSquaresGridShape;
  numbers: typeSquaresNumbers;
  squareKey: typeSquareKey;
}): { topDigits: readonly number[]; sideDigits: readonly number[] } {
  assertNumbers(input.numbers);
  const { shape, numbers } = input;
  const position = parseSquareKey({ squareKey: input.squareKey, shape });
  if (position === null) {
    return refuseInput(
      "squaresBadSquareKey",
      `${input.squareKey} is not a square on a ${String(shape.size)} grid.`,
    );
  }
  return {
    topDigits: numbers.colNumbers.slice(
      position.col * shape.digitsPerCol,
      (position.col + 1) * shape.digitsPerCol,
    ),
    sideDigits: numbers.rowNumbers.slice(
      position.row * shape.digitsPerRow,
      (position.row + 1) * shape.digitsPerRow,
    ),
  };
}

export const SQUARES_GRID_ACTIONS = [
  "take",
  "release",
  "assign",
  "lock",
  "unlock",
  "randomize",
  "clear",
  "publish",
  "unpublish",
  "copyFrom",
  "addWinner",
] as const;
export type typeSquaresGridAction = (typeof SQUARES_GRID_ACTIONS)[number];

export const SQUARES_GRID_REFUSALS = [
  "draft",
  "locked",
  "started",
  "scored",
  "final",
  "void",
  "notLocked",
  "alreadyPublished",
  "notPublished",
] as const;
export type typeSquaresGridRefusal = (typeof SQUARES_GRID_REFUSALS)[number];

export type typeSquaresGridPermission =
  { allowed: true } | { allowed: false; reason: typeSquaresGridRefusal };

const ALLOWED = { allowed: true } as const;

function deny(reason: typeSquaresGridRefusal): typeSquaresGridPermission {
  return { allowed: false, reason };
}

/** Started by the feed, by the live state, or by the clock passing kickoff. */
function isGridStarted(grid: typeSquaresGridFacts, nowMs: typeEpochMs) {
  return (
    grid.hasStarted ||
    grid.gridState === "live" ||
    (grid.firstKickoffAtMs !== null && nowMs >= grid.firstKickoffAtMs)
  );
}

/**
 * Whether a grid action is allowed by the grid's lifecycle alone. Role checks
 * (admin or member) stay in contest-api. Unpublish is blocked once started or
 * scored, clear after lock, randomize after start, take and release outside
 * an open published grid.
 *
 * Order: a void grid refuses everything. A winner may be added to any
 * published grid that has numbers (locked, live or final). A final grid
 * refuses everything else. Square changes (take, release, assign, clear,
 * copy) need an open grid before its lock time and before its game starts;
 * take and release also need it published.
 */
export function canMutateSquaresGrid(input: {
  grid: typeSquaresGridFacts;
  action: typeSquaresGridAction;
  nowMs: typeEpochMs;
}): typeSquaresGridPermission {
  const { grid, action, nowMs } = input;
  if (grid.gridState === "void") {
    return deny("void");
  }
  if (action === "addWinner") {
    if (grid.publishStatus === "draft") {
      return deny("draft");
    }
    return grid.gridState === "open" ? deny("notLocked") : ALLOWED;
  }
  if (grid.gridState === "final") {
    return deny("final");
  }
  const isStarted = isGridStarted(grid, nowMs);
  switch (action) {
    case "publish":
      return grid.publishStatus === "published"
        ? deny("alreadyPublished")
        : ALLOWED;
    case "unpublish":
      if (grid.publishStatus === "draft") {
        return deny("notPublished");
      }
      if (isStarted) {
        return deny("started");
      }
      return grid.hasWinners ? deny("scored") : ALLOWED;
    case "lock":
      return grid.gridState === "open" ? ALLOWED : deny("locked");
    case "unlock":
      if (grid.gridState === "open") {
        return deny("notLocked");
      }
      return isStarted ? deny("started") : ALLOWED;
    case "randomize":
      if (isStarted) {
        return deny("started");
      }
      return grid.hasWinners ? deny("scored") : ALLOWED;
    default:
      if (
        grid.publishStatus === "draft" &&
        (action === "take" || action === "release")
      ) {
        return deny("draft");
      }
      if (isStarted) {
        return deny("started");
      }
      return grid.gridState !== "open" ||
        (grid.lockAtMs !== null && nowMs >= grid.lockAtMs)
        ? deny("locked")
        : ALLOWED;
  }
}

export const SQUARES_TAKE_REFUSALS = [
  "gridClosed",
  "squareTaken",
  "notYourSquare",
  "limitReached",
  "notActiveMember",
  "badSquare",
] as const;
export type typeSquaresTakeRefusal = (typeof SQUARES_TAKE_REFUSALS)[number];

export type typeSquaresCellDecision =
  { allowed: true } | { allowed: false; reason: typeSquaresTakeRefusal };

function refuseCell(reason: typeSquaresTakeRefusal): typeSquaresCellDecision {
  return { allowed: false, reason };
}

function isSameOwner(left: typeSquaresOwner, right: typeSquaresOwner): boolean {
  return left.ownerKind === right.ownerKind && left.ownerId === right.ownerId;
}

function cellAt(
  squares: Readonly<Record<typeSquareKey, typeSquaresCell>>,
  squareKey: typeSquareKey,
): typeSquaresCell | null {
  return Object.hasOwn(squares, squareKey)
    ? (squares[squareKey] ?? null)
    : null;
}

function countOwnerSquares(
  squares: Readonly<Record<typeSquareKey, typeSquaresCell>>,
  owner: typeSquaresOwner,
): number {
  return Object.values(squares).filter((cell) => isSameOwner(cell, owner))
    .length;
}

function isOnGrid(grid: typeSquaresGridFacts, squareKey: typeSquareKey) {
  return (
    parseSquareKey({ squareKey, shape: buildSquaresGridShape(grid.size) }) !==
    null
  );
}

/** A member taking an empty square for themselves, within their limit. */
export function resolveSquareTake(input: {
  grid: typeSquaresGridFacts;
  squareKey: typeSquareKey;
  owner: typeSquaresOwner;
  maxPerMember: number | null;
  isActiveMember: boolean;
  nowMs: typeEpochMs;
}): typeSquaresCellDecision {
  const { grid, squareKey, owner, maxPerMember } = input;
  if (!isOnGrid(grid, squareKey)) {
    return refuseCell("badSquare");
  }
  if (!input.isActiveMember || owner.ownerKind !== "member") {
    return refuseCell("notActiveMember");
  }
  if (
    !canMutateSquaresGrid({ grid, action: "take", nowMs: input.nowMs }).allowed
  ) {
    return refuseCell("gridClosed");
  }
  if (cellAt(grid.squares, squareKey) !== null) {
    return refuseCell("squareTaken");
  }
  return maxPerMember !== null &&
    countOwnerSquares(grid.squares, owner) >= maxPerMember
    ? refuseCell("limitReached")
    : ALLOWED;
}

/** A member releasing their own square on an open grid. */
export function resolveSquareRelease(input: {
  grid: typeSquaresGridFacts;
  squareKey: typeSquareKey;
  owner: typeSquaresOwner;
  nowMs: typeEpochMs;
}): typeSquaresCellDecision {
  const { grid, squareKey } = input;
  if (!isOnGrid(grid, squareKey)) {
    return refuseCell("badSquare");
  }
  if (
    !canMutateSquaresGrid({ grid, action: "release", nowMs: input.nowMs })
      .allowed
  ) {
    return refuseCell("gridClosed");
  }
  const cell = cellAt(grid.squares, squareKey);
  return cell !== null && isSameOwner(cell, input.owner)
    ? ALLOWED
    : refuseCell("notYourSquare");
}

/**
 * An admin putting a member or a guest on a square, or emptying it
 * (`assignee` null). Admin assignment still honors the per-member limit.
 * Reassigning a held square is allowed; giving a square to its own holder
 * again is a no-op the limit does not count twice.
 */
export function resolveSquareAssign(input: {
  grid: typeSquaresGridFacts;
  squareKey: typeSquareKey;
  assignee: typeSquaresOwner | null;
  maxPerMember: number | null;
  nowMs: typeEpochMs;
}): typeSquaresCellDecision {
  const { grid, squareKey, assignee, maxPerMember } = input;
  if (!isOnGrid(grid, squareKey)) {
    return refuseCell("badSquare");
  }
  if (
    !canMutateSquaresGrid({ grid, action: "assign", nowMs: input.nowMs })
      .allowed
  ) {
    return refuseCell("gridClosed");
  }
  if (assignee === null) {
    return ALLOWED;
  }
  const cell = cellAt(grid.squares, squareKey);
  if (cell !== null && isSameOwner(cell, assignee)) {
    return ALLOWED;
  }
  return maxPerMember !== null &&
    countOwnerSquares(grid.squares, assignee) >= maxPerMember
    ? refuseCell("limitReached")
    : ALLOWED;
}

/** A square key's place in board order: row by row, left to right. */
function boardPosition(squareKey: typeSquareKey): number {
  const [row, col] = squareKey.split("-").map(Number);
  return (row ?? 0) * 100 + (col ?? 0);
}

function sortSquareKeys(keys: readonly typeSquareKey[]): typeSquareKey[] {
  return [...keys].sort(
    (left, right) => boardPosition(left) - boardPosition(right),
  );
}

/**
 * The squares a new grid starts with. "Carry squares forward" copies the
 * previous grid's owners who are still active members (and every guest not
 * removed); off starts empty. Each carried square is stamped as assigned by
 * the caller now.
 */
export function carryForwardSquares(input: {
  previousSquares: Readonly<Record<typeSquareKey, typeSquaresCell>>;
  activeMemberIds: readonly string[];
  activeGuestIds: readonly string[];
  assignedByUserId: string;
  nowMs: typeEpochMs;
}): Record<typeSquareKey, typeSquaresCell> {
  const activeMembers = new Set(input.activeMemberIds);
  const activeGuests = new Set(input.activeGuestIds);
  const carried: Record<typeSquareKey, typeSquaresCell> = {};
  for (const [squareKey, cell] of Object.entries(input.previousSquares)) {
    const isActive =
      cell.ownerKind === "member"
        ? activeMembers.has(cell.ownerId)
        : activeGuests.has(cell.ownerId);
    if (isActive) {
      carried[squareKey] = {
        ownerKind: cell.ownerKind,
        ownerId: cell.ownerId,
        assignedByUserId: input.assignedByUserId,
        assignedAtMs: input.nowMs,
      };
    }
  }
  return carried;
}

/**
 * "Copy squares from another grid": merge keeps this grid's owners, replace
 * drops them. Source squares go in board order. A source square is skipped
 * when merge finds it held by someone else here, or when its owner would pass
 * the per-member limit. A square already held by the same owner stays as is.
 */
export function copySquaresFromGrid(input: {
  targetSquares: Readonly<Record<typeSquareKey, typeSquaresCell>>;
  sourceSquares: Readonly<Record<typeSquareKey, typeSquaresCell>>;
  mode: "merge" | "replace";
  maxPerMember: number | null;
  assignedByUserId: string;
  nowMs: typeEpochMs;
}): {
  squares: Record<typeSquareKey, typeSquaresCell>;
  skippedSquareKeys: readonly typeSquareKey[];
} {
  const squares: Record<typeSquareKey, typeSquaresCell> =
    input.mode === "merge" ? { ...input.targetSquares } : {};
  const skippedSquareKeys: typeSquareKey[] = [];
  const sourceEntries = Object.entries(input.sourceSquares).sort(
    ([left], [right]) => boardPosition(left) - boardPosition(right),
  );
  for (const [squareKey, source] of sourceEntries) {
    const existing = cellAt(squares, squareKey);
    if (existing !== null) {
      if (!isSameOwner(existing, source)) {
        skippedSquareKeys.push(squareKey);
      }
      continue;
    }
    if (
      input.maxPerMember !== null &&
      countOwnerSquares(squares, source) >= input.maxPerMember
    ) {
      skippedSquareKeys.push(squareKey);
      continue;
    }
    squares[squareKey] = {
      ownerKind: source.ownerKind,
      ownerId: source.ownerId,
      assignedByUserId: input.assignedByUserId,
      assignedAtMs: input.nowMs,
    };
  }
  return { squares, skippedSquareKeys };
}

/**
 * The square keys to empty on each open grid when a member leaves or a guest
 * is removed. Locked, live, final and void grids keep everything. Grids with
 * nothing to release are left out.
 */
export function releaseOwnerSquares(input: {
  owner: typeSquaresOwner;
  grids: readonly {
    gridId: string;
    gridState: typeSquaresGridState;
    squares: Readonly<Record<typeSquareKey, typeSquaresCell>>;
  }[];
}): readonly { gridId: string; squareKeys: readonly typeSquareKey[] }[] {
  return input.grids
    .filter((grid) => grid.gridState === "open")
    .map((grid) => ({
      gridId: grid.gridId,
      squareKeys: sortSquareKeys(
        Object.entries(grid.squares)
          .filter(([, cell]) => isSameOwner(cell, input.owner))
          .map(([squareKey]) => squareKey),
      ),
    }))
    .filter((release) => release.squareKeys.length > 0);
}

/**
 * When a grid locks. Classic: its game's kickoff. Shared: the first tip-off of
 * its games. Null while no game is scheduled, and null with auto-lock off: then
 * the squaresAutoLock job still locks an open grid once its game has started,
 * regardless of this setting, so numbers exist before scoring.
 */
export function resolveSquaresGridLockAt(input: {
  gridType: typeSquaresGridType;
  autoLock: boolean;
  kickoffsAtMs: readonly typeEpochMs[];
}): typeEpochMs | null {
  const kickoffs = input.kickoffsAtMs.filter((value) => Number.isFinite(value));
  return input.autoLock && kickoffs.length > 0 ? Math.min(...kickoffs) : null;
}

/**
 * The numbers a grid gets at lock. The first lock of a season always draws.
 * Later grids redraw when `redrawNumbers` is on and reuse the season's first
 * draw when it is off. Shared grids never redraw between games.
 */
export function resolveSquaresLockNumbers(input: {
  redrawNumbers: boolean;
  seasonFirstDraw: typeSquaresNumbers | null;
  freshDraw: typeSquaresNumbers;
}): { numbers: typeSquaresNumbers; isSeasonFirstDraw: boolean } {
  if (input.seasonFirstDraw === null) {
    return { numbers: input.freshDraw, isSeasonFirstDraw: true };
  }
  return {
    numbers: input.redrawNumbers ? input.freshDraw : input.seasonFirstDraw,
    isSeasonFirstDraw: false,
  };
}

/** Whether a new season may start: every published grid is final or void. */
export function canStartNewSquaresSeason(input: {
  grids: readonly {
    publishStatus: typeSquaresPublishStatus;
    gridState: typeSquaresGridState;
  }[];
}): { allowed: true } | { allowed: false; openGridCount: number } {
  const openGridCount = input.grids.filter(
    (grid) =>
      grid.publishStatus === "published" &&
      grid.gridState !== "final" &&
      grid.gridState !== "void",
  ).length;
  return openGridCount === 0 ? ALLOWED : { allowed: false, openGridCount };
}
